// The windows a pack's session limit counts over, for the slot pickers.
//
// Mirrors the backend gate (functions/src/helpers/allowanceWindow.ts findAllowanceBreach):
//   per Day / Week / Month   calendar days, Monday-start weeks, months
//   per billing period       each billing period of a recurring CUSTOM pack, walked from the
//                            booking's start in steps of "bill every" (the invoice periods)
//   for the whole pack       every session counts once (sessionLimitUnit "PACK")
// A pack saved without a unit is read as sessionLimitUnitOf says (prepaid: whole pack).
// One-time packs, and bookings made before this counting existed (periodCounting false), keep
// the caller's legacy windows -- the "Pack runs for" windows the pickers have always used.
import { getInstance, getInstanceClone } from "./timeutil";
import { sessionLimitUnitOf } from "./allowanceBlocks";

export type LimitWindow = { start: Date; end: Date; days: Date[] };

/** The mark the backend stamps on bookings made under per-period counting. */
export const PERIOD_COUNTING = "PERIOD";

const CALENDAR: Record<string, "day" | "isoWeek" | "month"> = { DAY: "day", WEEK: "isoWeek", MONTH: "month" };
const SPAN: Record<string, "day" | "week" | "month"> = { DAYS: "day", WEEKS: "week", MONTHS: "month" };

export function limitWindows(
  days: Date[],
  pricingPack: any,
  opts: { periodCounting: boolean; anchor?: number | null; legacy: (days: Date[]) => LimitWindow[] }
): LimitWindow[] {
  if (!opts.periodCounting) return opts.legacy(days);
  const sorted = [...days].sort((a, b) => a.getTime() - b.getTime());
  if (!sorted.length) return [];
  const unit = sessionLimitUnitOf(pricingPack);

  const calendarUnit = unit ? CALENDAR[unit] : undefined;
  if (calendarUnit) {
    const windows = new Map<number, LimitWindow>();
    sorted.forEach((day) => {
      const start = getInstanceClone(getInstance(day)).startOf(calendarUnit);
      const key = start.valueOf();
      if (!windows.has(key)) {
        windows.set(key, { start: start.toDate(), end: getInstanceClone(start).endOf(calendarUnit).toDate(), days: [] });
      }
      windows.get(key)!.days.push(day);
    });
    return Array.from(windows.values());
  }

  const recurringCustom = pricingPack?.paymentType === "RECURRING" && pricingPack?.frequency === "CUSTOM";
  if (!recurringCustom) return opts.legacy(days);

  if (unit === "PACK") {
    return [{
      start: getInstanceClone(getInstance(sorted[0])).startOf("day").toDate(),
      end: getInstanceClone(getInstance(sorted[sorted.length - 1])).endOf("day").toDate(),
      days: sorted,
    }];
  }

  // Per billing period (CYCLE).
  const value = Number(pricingPack.billEveryValue ?? pricingPack.durationValue) || 1;
  const step = SPAN[String(pricingPack.billEveryUnit || pricingPack.durationUnit || "MONTHS").toUpperCase()] || "month";
  const firstMs = opts.anchor != null ? Math.min(opts.anchor, sorted[0].getTime()) : sorted[0].getTime();
  let start = getInstanceClone(getInstance(firstMs)).startOf("day");
  let end = getInstanceClone(start).add(value, step);
  const windows: LimitWindow[] = [];
  let i = 0;
  // Bounded: one step per billing period up to the last selected day.
  for (let guard = 0; i < sorted.length && guard < 5000; guard++) {
    if (sorted[i].getTime() >= end.valueOf()) {
      start = end;
      end = getInstanceClone(start).add(value, step);
      continue;
    }
    const window: LimitWindow = { start: start.toDate(), end: getInstanceClone(end).subtract(1, "ms").toDate(), days: [] };
    while (i < sorted.length && sorted[i].getTime() < end.valueOf()) window.days.push(sorted[i++]);
    windows.push(window);
  }
  return windows;
}
